import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Input, Select, Tag, Drawer, Timeline, Space, Button,
  message, Empty
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import { adminAPI } from '../api/admin';

const STAGE_LABELS = {
  waitlisted: 'ثبت در لیست انتظار',
  invited: 'دعوت شده',
  secret_message_entered: 'ورود به پیام محرمانه',
  phone_submitted: 'ثبت شماره تماس',
  phone_verified: 'تایید شماره تماس',
  birthdate_submitted: 'ثبت تاریخ تولد',
  site_user: 'کاربر سایت',
  platform_crossed: 'عبور از سکوی ۹ و ۳/۴',
  kings_cross_arrived: 'ورود به کینگزکراس'
};

const STAGE_COLORS = {
  waitlisted: 'default',
  invited: 'blue',
  secret_message_entered: 'cyan',
  phone_submitted: 'geekblue',
  phone_verified: 'purple',
  birthdate_submitted: 'gold',
  site_user: 'green',
  platform_crossed: 'volcano',
  kings_cross_arrived: 'magenta'
};

const TIMELINE_LABELS = {
  waitlist_joined: 'ثبت در لیست انتظار',
  invitation_sent: 'دعوت‌نامه ارسال شد',
  invitation_claimed: 'دعوت‌نامه claim شد',
  secret_message_viewed: 'مشاهده پیام محرمانه',
  phone_submitted: 'ثبت شماره تماس',
  phone_verified: 'تایید شماره تماس',
  phone_changed: 'تغییر شماره تماس',
  birthdate_submitted: 'ثبت تاریخ تولد',
  site_registered: 'عضویت در سایت',
  site_login: 'ورود به سایت',
  platform_attempt: 'تلاش برای عبور از سکو',
  platform_success: 'عبور موفق از سکو',
  platform_fail: 'شکست در عبور از سکو',
  kings_cross_arrived: 'ورود به کینگزکراس',
  journey_changed: 'تغییر مرحله Journey',
  tag_added: 'افزودن Tag',
  tag_removed: 'حذف Tag',
  attempts_increased: 'افزایش سقف تلاش',
  train_schedule_changed: 'تغییر زمان حرکت قطار',
  lead_captured: 'ثبت به‌عنوان لید (بدون کد دعوت)',
  other: 'رویداد دیگر'
};

export default function PersonsManager() {
  const adminCookie = (() => { try { return JSON.parse(Cookies.get('admin') || '{}'); } catch { return {}; } })();
  const isTeacher = (adminCookie.role || 'admin') === 'teacher';

  const [persons, setPersons] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', journeyStage: undefined, tag: undefined });
  const [availableTags, setAvailableTags] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [newTagKey, setNewTagKey] = useState(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.journeyStage) params.journeyStage = filters.journeyStage;
      if (filters.tag) params.tag = filters.tag;
      const res = await adminAPI.getPersons(params);
      if (res.data.success) {
        setPersons(res.data.persons || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch {
      message.error('خطا در بارگذاری لیست افراد');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminAPI.getAppConfig('user_tags').then((res) => {
      if (res.data.success) setAvailableTags(res.data.config?.value || []);
    }).catch(() => {});
  }, []);

  const openDrawer = async (person) => {
    setSelected(person);
    setDrawerOpen(true);
    try {
      const res = await adminAPI.getPerson(person._id);
      if (res.data.success) {
        setSelected(res.data.person);
        setTimelineData(res.data.timeline || []);
        setAvailableTags(res.data.availableTags || availableTags);
      }
    } catch {
      message.error('خطا در بارگذاری جزئیات');
    }
  };

  const addTag = async (tagKey) => {
    if (!tagKey || !selected) return;
    try {
      const res = await adminAPI.addPersonTag(selected._id, tagKey);
      setSelected((s) => ({ ...s, tags: res.data.tags }));
      setNewTagKey(undefined);
      load();
    } catch {
      message.error('خطا در افزودن Tag');
    }
  };

  const removeTag = async (tagKey) => {
    if (!selected) return;
    try {
      const res = await adminAPI.removePersonTag(selected._id, tagKey);
      setSelected((s) => ({ ...s, tags: res.data.tags }));
      load();
    } catch {
      message.error('خطا در حذف Tag');
    }
  };

  const tagLabel = (key) => availableTags.find((t) => t.key === key)?.label || key;
  const tagColor = (key) => availableTags.find((t) => t.key === key)?.color || '#888';

  const columns = [
    {
      title: 'نام',
      key: 'name',
      render: (_, p) => `${p.firstName || '—'} ${p.lastName || ''}`.trim() || '—'
    },
    {
      title: 'شماره تماس',
      dataIndex: 'phoneNumber',
      render: (v) => <span style={{ direction: 'ltr', display: 'inline-block' }}>{v}</span>
    },
    {
      title: 'مرحله Journey',
      dataIndex: 'journeyStage',
      render: (v) => <Tag color={STAGE_COLORS[v] || 'default'}>{STAGE_LABELS[v] || v}</Tag>
    },
    {
      title: 'Tagها',
      dataIndex: 'tags',
      render: (tags) => (
        <Space wrap size={4}>
          {(tags || []).map((t) => <Tag key={t} color={tagColor(t)}>{tagLabel(t)}</Tag>)}
        </Space>
      )
    },
    {
      title: 'تاریخ ثبت',
      dataIndex: 'createdAt',
      render: (v) => new Date(v).toLocaleDateString('fa-IR')
    },
    {
      title: 'تلاش‌های سکو',
      dataIndex: 'platformAttempts'
    },
    {
      title: '',
      key: 'action',
      render: (_, p) => <Button size="small" onClick={() => openDrawer(p)}>مشاهده Timeline</Button>
    }
  ];

  return (
    <Card title="کاربران — Journey / Tag / Timeline">
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="جستجو (نام یا شماره)"
          prefix={<SearchOutlined />}
          style={{ width: 220 }}
          onPressEnter={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />
        <Select
          allowClear
          placeholder="فیلتر بر اساس Journey"
          style={{ width: 220 }}
          value={filters.journeyStage}
          onChange={(v) => setFilters((f) => ({ ...f, journeyStage: v }))}
          options={Object.entries(STAGE_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="فیلتر بر اساس Tag"
          style={{ width: 180 }}
          value={filters.tag}
          onChange={(v) => setFilters((f) => ({ ...f, tag: v }))}
          options={availableTags.map((t) => ({ value: t.key, label: t.label }))}
        />
        <Button icon={<ReloadOutlined />} onClick={load}>بارگذاری مجدد</Button>
      </Space>

      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={persons}
        pagination={{ current: page, total, pageSize: 20, onChange: setPage }}
        locale={{ emptyText: <Empty description="کسی پیدا نشد" /> }}
      />

      <Drawer
        title={selected ? `${selected.firstName || ''} ${selected.lastName || ''}` : ''}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
      >
        {selected && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#888', fontSize: 13 }}>شماره تماس</div>
              <div style={{ direction: 'ltr', textAlign: 'right', fontWeight: 600 }}>{selected.phoneNumber}</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#888', fontSize: 13, marginBottom: 6 }}>مرحله فعلی Journey</div>
              <Tag color={STAGE_COLORS[selected.journeyStage] || 'default'} style={{ fontSize: 14, padding: '4px 10px' }}>
                {STAGE_LABELS[selected.journeyStage] || selected.journeyStage}
              </Tag>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#888', fontSize: 13, marginBottom: 6 }}>Tagها</div>
              <Space wrap style={{ marginBottom: 8 }}>
                {(selected.tags || []).map((t) => (
                  <Tag key={t} color={tagColor(t)} closable={!isTeacher} onClose={(e) => { e.preventDefault(); removeTag(t); }}>
                    {tagLabel(t)}
                  </Tag>
                ))}
              </Space>
              {!isTeacher && (
                <Space>
                  <Select
                    style={{ width: 180 }}
                    placeholder="افزودن Tag"
                    value={newTagKey}
                    onChange={setNewTagKey}
                    options={availableTags
                      .filter((t) => !(selected.tags || []).includes(t.key))
                      .map((t) => ({ value: t.key, label: t.label }))}
                  />
                  <Button icon={<PlusOutlined />} onClick={() => addTag(newTagKey)} disabled={!newTagKey}>افزودن</Button>
                </Space>
              )}
            </div>

            <div style={{ color: '#888', fontSize: 13, marginBottom: 10 }}>Timeline کامل</div>
            <Timeline
              items={timelineData.map((t) => ({
                children: (
                  <div>
                    <div style={{ fontWeight: 600 }}>{TIMELINE_LABELS[t.type] || t.type}</div>
                    {t.message && <div style={{ color: '#888', fontSize: 13 }}>{t.message}</div>}
                    <div style={{ color: '#aaa', fontSize: 12 }}>{new Date(t.createdAt).toLocaleString('fa-IR')}</div>
                  </div>
                )
              }))}
            />
            {timelineData.length === 0 && <Empty description="هنوز رویدادی ثبت نشده" />}
          </>
        )}
      </Drawer>
    </Card>
  );
}
